import { useDeferredValue, useState } from "react";
import { CheckIcon, ExternalLinkIcon, LinkIcon, LockKeyholeIcon } from "lucide-react";
import { GithubIcon } from "~/components/icons/GithubIcon";
import { getGitHubRepoAccessLabel, useGitHubRepos } from "~/hooks/useGitHubRepos";
import { Button } from "../ui/button";
import { CommandDialog, CommandDialogPopup } from "../ui/command";

const GITHUB_REPOSITORY_PART = /^[A-Za-z0-9_.-]+$/;

export function normalizeGitHubRepositoryUrl(value: string): string | null {
  const trimmed = value.trim().replace(/\/+$/, "");
  const githubPrefix = "https://github.com/";
  const path = trimmed.toLowerCase().startsWith(githubPrefix)
    ? trimmed.slice(githubPrefix.length)
    : trimmed.includes("://")
      ? null
      : trimmed;
  if (path === null) return null;
  const parts = path.split("/");
  if (parts.length !== 2) return null;
  const owner = parts[0]?.trim() ?? "";
  const repository = (parts[1]?.trim() ?? "").replace(/\.git$/i, "");
  if (!GITHUB_REPOSITORY_PART.test(owner) || !GITHUB_REPOSITORY_PART.test(repository)) return null;
  return `https://github.com/${owner}/${repository}`;
}

function repositoryLabel(url: string): string {
  return url.replace(/^https:\/\/github\.com\//i, "").replace(/\.git$/i, "");
}

export type ProjectRepositoryBindingState = "unattached" | "attached" | "unavailable";

export interface ProjectRepositoryValueProps {
  readonly githubRepoUrl: string | null | undefined;
  readonly bindingState: ProjectRepositoryBindingState;
  readonly workspaceId: string | null | undefined;
  readonly density?: "default" | "compact";
  readonly onSave: (githubRepoUrl: string) => Promise<void>;
}

function RepositoryLinkPalette(props: {
  readonly open: boolean;
  readonly workspaceId: string | null | undefined;
  readonly currentUrl: string | null | undefined;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSave: (githubRepoUrl: string) => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [savingUrl, setSavingUrl] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const inventory = useGitHubRepos(useDeferredValue(query), {
    enabled: props.open,
    ...(props.workspaceId === undefined ? {} : { organizationId: props.workspaceId }),
  });
  const close = () => {
    if (savingUrl !== null) return;
    setQuery("");
    setSaveError(null);
    props.onOpenChange(false);
  };
  const selectRepository = async (url: string) => {
    setSavingUrl(url);
    setSaveError(null);
    try {
      await props.onSave(url);
      props.onOpenChange(false);
      setQuery("");
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "Could not link this repository.");
    } finally {
      setSavingUrl(null);
    }
  };

  return (
    <CommandDialog open={props.open} onOpenChange={(open) => (open ? undefined : close())}>
      <CommandDialogPopup aria-label="Link GitHub repository">
        <div className="relative z-10 flex min-h-0 flex-col overflow-hidden rounded-2xl">
          <header className="border-b px-4 py-3">
            <h2 className="text-sm font-semibold">Link GitHub repository</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Search repositories available to this project&apos;s workspace.
            </p>
          </header>
          <div className="border-b p-3">
            <input
              autoFocus
              aria-label="Search GitHub repositories"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search owner or repository"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25"
            />
          </div>
          <div className="min-h-40 overflow-y-auto p-2">
            {inventory.loading ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                Loading repositories…
              </p>
            ) : inventory.error ? (
              <div className="px-2 py-5 text-center">
                <p className="text-sm text-destructive">{inventory.error}</p>
                <Button className="mt-3" size="sm" variant="outline" onClick={inventory.refetch}>
                  Retry
                </Button>
              </div>
            ) : inventory.repos.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                {inventory.github_connected
                  ? "No accessible repositories match this search."
                  : "Connect GitHub in Settings to choose a repository."}
              </p>
            ) : (
              <ul className="space-y-1" aria-label="GitHub repositories">
                {inventory.repos.map((repository) => {
                  const selected =
                    repository.html_url.toLowerCase() === props.currentUrl?.toLowerCase();
                  const saving = savingUrl === repository.html_url;
                  return (
                    <li key={repository.full_name}>
                      <button
                        type="button"
                        disabled={savingUrl !== null || selected}
                        onClick={() => void selectRepository(repository.html_url)}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <GithubIcon className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {repository.full_name}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {getGitHubRepoAccessLabel(repository)}
                            {repository.private ? " · Private" : " · Public"}
                          </span>
                        </span>
                        {selected ? (
                          <CheckIcon
                            className="size-4 text-primary"
                            aria-label="Currently linked"
                          />
                        ) : null}
                        {saving ? (
                          <span className="text-xs text-muted-foreground">Linking…</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {saveError ? (
            <p className="border-t px-4 py-3 text-sm text-destructive">{saveError}</p>
          ) : null}
        </div>
      </CommandDialogPopup>
    </CommandDialog>
  );
}

function LinkedRepositoryTrigger(props: {
  readonly url: string;
  readonly bindingState: ProjectRepositoryBindingState;
  readonly density: "default" | "compact";
  readonly onEdit: () => void;
}) {
  const label = repositoryLabel(props.url);
  return (
    <span className="flex w-full min-w-0 items-center gap-1.5">
      {props.density === "default" ? (
        <GithubIcon className="size-3.5 shrink-0 text-muted-foreground" />
      ) : null}
      <a
        href={props.url}
        target="_blank"
        rel="noreferrer"
        title={label}
        className="min-w-0 flex-1 truncate text-primary hover:underline"
      >
        {label}
      </a>
      <a
        href={props.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${label} on GitHub`}
        className="inline-flex size-5 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <ExternalLinkIcon className="size-3" aria-hidden />
      </a>
      {props.bindingState === "unattached" ? (
        <button
          type="button"
          onClick={props.onEdit}
          className="shrink-0 text-xs text-primary hover:underline"
        >
          Change
        </button>
      ) : props.bindingState === "attached" ? (
        <span
          className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
          title="Repository is bound to an attached Forge execution target"
        >
          <LockKeyholeIcon className="size-3" aria-hidden /> Bound
        </span>
      ) : null}
    </span>
  );
}

function EmptyRepositoryTrigger(props: {
  readonly bindingState: ProjectRepositoryBindingState;
  readonly onEdit: () => void;
}) {
  return props.bindingState === "unattached" ? (
    <button
      type="button"
      onClick={props.onEdit}
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      <LinkIcon className="size-3" /> Link repository
    </button>
  ) : (
    <span className="text-muted-foreground">
      {props.bindingState === "unavailable" ? "Repository status unavailable" : "Not set"}
    </span>
  );
}

export function ProjectRepositoryValue(props: ProjectRepositoryValueProps) {
  const [open, setOpen] = useState(false);
  const trigger = props.githubRepoUrl ? (
    <LinkedRepositoryTrigger
      url={props.githubRepoUrl}
      bindingState={props.bindingState}
      density={props.density ?? "default"}
      onEdit={() => setOpen(true)}
    />
  ) : (
    <EmptyRepositoryTrigger bindingState={props.bindingState} onEdit={() => setOpen(true)} />
  );
  return (
    <>
      {trigger}
      <RepositoryLinkPalette
        open={open}
        workspaceId={props.workspaceId}
        currentUrl={props.githubRepoUrl}
        onOpenChange={setOpen}
        onSave={props.onSave}
      />
    </>
  );
}
